'use client'

import type { ComponentProps } from 'react'

import { Avatar as AvatarPrimitive } from '@base-ui/react/avatar'
import { UserIcon } from '@/components/icons/icon-set'
import { cn } from '@/lib/utils'
import { avatarPhoto } from '@/constants/avatars'

type AvatarSize = 'xs' | 'sm' | 'default' | 'lg' | 'xl'

const sizeClasses: Record<AvatarSize, string> = {
    xs: 'size-5 [&_svg]:size-3',
    sm: 'size-6 [&_svg]:size-3.5',
    default: 'size-8 [&_svg]:size-4',
    lg: 'size-9 [&_svg]:size-5',
    xl: 'size-10 [&_svg]:size-5'
}

type AvatarProps = Omit<AvatarPrimitive.Root.Props, 'className'> & {
    className?: string
    size?: AvatarSize
}

/*
 * GoodUnited / Avatar. Figma: 10 / Components / Avatar.
 * Neutral placeholder uses Colors/avatar (background and foreground). Photos and logos sit in AvatarImage.
 */
function Avatar({ className, size = 'default', ...props }: AvatarProps) {
    return (
        <AvatarPrimitive.Root
            data-slot='avatar'
            data-size={size}
            className={cn('relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-avatar-background text-avatar-foreground select-none', sizeClasses[size], className)}
            {...props}
        />
    )
}

/* Pass a photo, or use photo on Avatar for a sample person. */
function AvatarImage({ className, ...props }: Omit<AvatarPrimitive.Image.Props, 'className'> & { className?: string }) {
    return (
        <AvatarPrimitive.Image
            data-slot='avatar-image'
            className={cn('size-full object-cover', className)}
            {...props}
        />
    )
}

function AvatarFallback({ className, children, ...props }: Omit<AvatarPrimitive.Fallback.Props, 'className'> & { className?: string }) {
    return (
        <AvatarPrimitive.Fallback
            data-slot='avatar-fallback'
            className={cn('flex size-full items-center justify-center text-xs font-medium', className)}
            {...props}
        >
            {children ?? <UserIcon aria-hidden='true' />}
        </AvatarPrimitive.Fallback>
    )
}

interface AvatarGroupProps extends ComponentProps<'div'> {
    count?: number
    size?: AvatarSize
    label?: string
}

/* AvatarGroup: overlapping placeholders for a set of people. */
function AvatarGroup({ count = 5, size = 'default', label, className, children, ...props }: AvatarGroupProps) {
    return (
        <div
            data-slot='avatar-group'
            role='group'
            aria-label={label ?? `${count} people`}
            className={cn('flex items-center -space-x-2 *:data-[slot=avatar]:ring-2 *:data-[slot=avatar]:ring-surface-raised', size === 'xl' && '-space-x-3', className)}
            {...props}
        >
            {children ?? Array.from({ length: count }, (_, index) => (
                <Avatar key={index} size={size}>
                    <AvatarImage src={avatarPhoto(index + 1)} alt='' />
                    <AvatarFallback />
                </Avatar>
            ))}
        </div>
    )
}

export { Avatar, AvatarFallback, AvatarGroup, AvatarImage }
export type { AvatarSize }
